import { statusLabels } from '@/constants'
import { useRole } from '@/hooks/useRole'
import { Article, Order, Service, SharedProps } from '@/types'
import { formatCurrency, formatStatus, getStatusColor } from '@/utils/format'
import { Head, Link, router } from '@inertiajs/react'
import {
  ActionIcon,
  Badge,
  Center,
  Group,
  Menu,
  Pagination,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from '@mantine/core'
import { useDebouncedCallback } from '@mantine/hooks'
import {
  IconArrowDown,
  IconArrowUp,
  IconCircleCheck,
  IconClock,
  IconDotsVertical,
  IconEdit,
  IconPrinter,
  IconScissors,
  IconSearch,
  IconShirt,
  IconX,
} from '@tabler/icons-react'
import dayjs from 'dayjs'
import { ComponentType, useState } from 'react'

type SortColumn = 'planned_date' | 'id' | 'reference'
type SortValue = `${SortColumn}_asc` | `${SortColumn}_desc` | null
const DEFAULT_SORT = 'id_desc' as const satisfies NonNullable<SortValue>

interface Filters {
  search: string | null
  planned_date: string | null
  status: string | null
  client_kind: string | null
  payment_status: string | null
  sort: SortValue
}

interface PaginationMeta {
  current_page: number
  total_pages: number
  total_count: number
  per_page: number
}

// Aggregated over every order matching the active filters (not just the current
// page), so the counts reflect all the work due for the selected date.
interface Summary {
  articles_count: number
  services_count: number
  services_pending_count: number
}

interface ConfirmedOrdersProps extends SharedProps {
  orders: Order[]
  filters: Filters
  pagination: PaginationMeta
  summary: Summary
}

type SummaryIcon = ComponentType<{ size?: number | string }>

function SummaryStat({
  label,
  value,
  icon: Icon,
  color,
}: {
  label: string
  value: number
  icon: SummaryIcon
  color: string
}) {
  return (
    <Group gap="sm" wrap="nowrap">
      <ThemeIcon variant="light" color={color} size="lg" radius="md">
        <Icon size={20} />
      </ThemeIcon>
      <div>
        <Text fz="xl" fw={700} lh={1.1}>
          {value}
        </Text>
        <Text size="xs" c="dimmed">
          {label}
        </Text>
      </div>
    </Group>
  )
}

// Renders all <tr> rows for one order, with rowSpan-anchored cells on the first
// row of the order (order-level cells) and the first row of each article
// (article-level cells). Returns a fragment so the rows sit directly under <tbody>.
function OrderRows({
  order,
  isBoutique,
  organizationSlug,
}: {
  order: Order
  isBoutique: boolean
  organizationSlug: string | undefined
}) {
  const articles: (Article | null)[] = order.articles.length === 0 ? [null] : order.articles
  const rows = articles.flatMap((article) => {
    const services: (Service | null)[] = article === null || article.services.length === 0 ? [null] : article.services
    return services.map((service, serviceIndex) => ({
      article,
      service,
      isFirstInArticle: serviceIndex === 0,
      articleRowSpan: services.length,
    }))
  })

  const orderRowSpan = rows.length

  const navigateToOrder = () => router.get(`/organizations/${organizationSlug}/orders/${order.id}`)

  return (
    <>
      {rows.map((row, index) => {
        const { article, service, isFirstInArticle, articleRowSpan } = row
        const isFirstInOrder = index === 0
        const rowKey = `${order.id}-${article?.id ?? 'na'}-${service?.id ?? 'na'}-${index}`

        return (
          <Table.Tr
            key={rowKey}
            onClick={navigateToOrder}
            style={{
              cursor: 'pointer',
              borderTop: isFirstInOrder ? '2px solid var(--mantine-color-gray-3)' : undefined,
            }}
          >
            {isFirstInOrder && <Table.Td rowSpan={orderRowSpan}>#{order.id}</Table.Td>}
            {isFirstInOrder && <Table.Td rowSpan={orderRowSpan}>{order.customer_name}</Table.Td>}
            {isFirstInArticle && (
              <Table.Td rowSpan={articleRowSpan}>
                <Title order={6} size="sm">
                  {article?.article_name ?? '—'}
                </Title>
              </Table.Td>
            )}
            {isFirstInArticle && <Table.Td rowSpan={articleRowSpan}>{article?.reference || '—'}</Table.Td>}
            <Table.Td>
              {service ? (
                <Badge variant="dot" size="xs" color="gray">
                  {service.service_name}
                </Badge>
              ) : (
                <Text size="xs" c="dimmed">
                  —
                </Text>
              )}
            </Table.Td>
            <Table.Td>
              {service ? (
                <Badge
                  color={service.completed_at ? 'green' : 'gray'}
                  variant={service.completed_at ? 'filled' : 'light'}
                  size="sm"
                >
                  {service.completed_at ? 'Fait' : 'À faire'}
                </Badge>
              ) : (
                <Text size="xs" c="dimmed">
                  —
                </Text>
              )}
            </Table.Td>
            <Table.Td>{service ? formatCurrency(service.price) : '—'}</Table.Td>
            {isFirstInOrder && (
              <Table.Td rowSpan={orderRowSpan} visibleFrom="sm">
                {order.planned_date ? dayjs(order.planned_date).format('DD/MM/YYYY') : '—'}
              </Table.Td>
            )}
            {isFirstInOrder && (
              <Table.Td rowSpan={orderRowSpan}>
                <Badge color={getStatusColor(order, isBoutique)} variant="light">
                  {formatStatus(order, isBoutique)}
                </Badge>
              </Table.Td>
            )}
            {isFirstInOrder && (
              <Table.Td rowSpan={orderRowSpan} onClick={(event) => event.stopPropagation()}>
                <Menu shadow="md" position="bottom-end">
                  <Menu.Target>
                    <ActionIcon variant="subtle" color="gray">
                      <IconDotsVertical size={16} />
                    </ActionIcon>
                  </Menu.Target>

                  <Menu.Dropdown>
                    <Menu.Item
                      leftSection={<IconEdit size={14} />}
                      component={Link}
                      href={`/organizations/${organizationSlug}/orders/${order.id}/edit`}
                    >
                      Modifier
                    </Menu.Item>
                    <Menu.Item
                      leftSection={<IconPrinter size={14} />}
                      component={Link}
                      href={`/organizations/${organizationSlug}/orders/${order.id}/print`}
                    >
                      Imprimer
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </Table.Td>
            )}
          </Table.Tr>
        )
      })}
    </>
  )
}

export default function ConfirmedOrdersIndex({
  orders,
  filters,
  pagination,
  summary,
  current_organization,
}: ConfirmedOrdersProps) {
  const { isBoutique } = useRole()
  const [searchInput, setSearchInput] = useState(filters.search ?? '')

  const basePath = `/organizations/${current_organization?.slug}/confirmed_orders`

  const updateFilters = (updates: Partial<Filters & { page: number }>) => {
    const next = {
      search: filters.search ?? undefined,
      planned_date: filters.planned_date ?? undefined,
      status: filters.status ?? undefined,
      client_kind: filters.client_kind ?? undefined,
      payment_status: filters.payment_status ?? undefined,
      sort: filters.sort ?? undefined,
      page: undefined as number | undefined,
      ...updates,
    }
    const cleaned = Object.fromEntries(
      Object.entries(next).filter(([, value]) => value !== undefined && value !== null && value !== '')
    )
    router.get(basePath, cleaned, { preserveState: true, preserveScroll: true })
  }

  const submitSearch = useDebouncedCallback((value: string) => {
    const trimmed = value.trim()
    if (trimmed === (filters.search ?? '')) return
    updateFilters({ search: trimmed || undefined, page: 1 })
  }, 300)

  const handleSearchChange = (value: string) => {
    setSearchInput(value)
    submitSearch(value)
  }

  const clearSearch = () => handleSearchChange('')

  const handlePlannedDateChange = (value: string) => updateFilters({ planned_date: value || undefined, page: 1 })

  const handleStatusChange = (value: string | null) => updateFilters({ status: value ?? undefined, page: 1 })

  const handleClientKindChange = (value: string | null) => updateFilters({ client_kind: value ?? undefined, page: 1 })

  const handlePaymentStatusChange = (value: string | null) =>
    updateFilters({ payment_status: value ?? undefined, page: 1 })

  const effectiveSort: NonNullable<SortValue> = filters.sort ?? DEFAULT_SORT

  const toggleSort = (column: SortColumn) => {
    const next: SortValue = effectiveSort === `${column}_asc` ? `${column}_desc` : `${column}_asc`
    updateFilters({ sort: next, page: 1 })
  }

  const renderSortIndicators = (column: SortColumn) => (
    <Group gap={0} wrap="nowrap" style={{ flexShrink: 0 }}>
      <IconArrowUp
        size={14}
        stroke={3}
        color={effectiveSort === `${column}_desc` ? 'var(--mantine-color-brand-filled)' : 'var(--mantine-color-gray-4)'}
      />
      <IconArrowDown
        size={14}
        stroke={3}
        color={effectiveSort === `${column}_asc` ? 'var(--mantine-color-brand-filled)' : 'var(--mantine-color-gray-4)'}
        style={{ marginLeft: -4 }}
      />
    </Group>
  )

  const handlePageChange = (page: number) => updateFilters({ page })

  const servicesDoneCount = summary.services_count - summary.services_pending_count

  const summaryTitle = filters.planned_date
    ? `À remettre le ${dayjs(filters.planned_date).format('DD/MM/YYYY')}`
    : 'Récapitulatif'

  return (
    <>
      <Head title={`Commandes - ${current_organization?.name}`} />

      <Stack gap="md">
        <Title>Commandes</Title>

        <Group align="end">
          <TextInput
            label="Date de remise"
            type="date"
            value={filters.planned_date ?? ''}
            onChange={(event) => handlePlannedDateChange(event.target.value)}
            w={180}
            rightSection={
              filters.planned_date ? (
                <ActionIcon size="sm" variant="subtle" color="gray" onClick={() => handlePlannedDateChange('')}>
                  <IconX size={14} />
                </ActionIcon>
              ) : null
            }
          />

          <Select
            label="Statut"
            placeholder="Statut"
            data={Object.entries(statusLabels)
              .filter(([key]) => key !== 'draft')
              .map(([key, label]) => ({
                label,
                value: key,
              }))}
            value={filters.status}
            onChange={handleStatusChange}
            clearable
            w={160}
          />

          <Select
            label="Type Client"
            placeholder="Type"
            data={[
              { value: 'particulier', label: 'Particulier' },
              { value: 'societe', label: 'Société' },
            ]}
            value={filters.client_kind}
            onChange={handleClientKindChange}
            clearable
            w={160}
          />

          <Select
            label="Paiement"
            placeholder="Paiement"
            data={[
              { value: 'paid', label: 'Payé' },
              { value: 'unpaid', label: 'Non payé' },
            ]}
            value={filters.payment_status}
            onChange={handlePaymentStatusChange}
            clearable
            w={160}
          />

          <TextInput
            label="Recherche"
            placeholder="Client, article, service..."
            leftSection={<IconSearch size={16} />}
            value={searchInput}
            onChange={(event) => handleSearchChange(event.target.value)}
            rightSection={
              searchInput ? (
                <ActionIcon size="sm" variant="subtle" color="gray" onClick={clearSearch}>
                  <IconX size={14} />
                </ActionIcon>
              ) : null
            }
            style={{ flex: 1 }}
          />
        </Group>

        <Paper withBorder radius="md" p="md">
          <Stack gap="sm">
            <Text size="sm" fw={600} c="dimmed">
              {summaryTitle}
            </Text>
            <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="lg">
              <SummaryStat label="Articles" value={summary.articles_count} icon={IconShirt} color="blue" />
              <SummaryStat label="Services" value={summary.services_count} icon={IconScissors} color="grape" />
              <SummaryStat label="À faire" value={summary.services_pending_count} icon={IconClock} color="orange" />
              <SummaryStat label="Faits" value={servicesDoneCount} icon={IconCircleCheck} color="green" />
            </SimpleGrid>
          </Stack>
        </Paper>

        <Paper withBorder radius="md" p={{ base: 0, sm: 'md' }}>
          <Table.ScrollContainer minWidth={1200}>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={70}>
                    <Group gap={4} style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('id')}>
                      <span>N°</span>
                      {renderSortIndicators('id')}
                    </Group>
                  </Table.Th>
                  <Table.Th>Client</Table.Th>
                  <Table.Th miw={180}>Article</Table.Th>
                  <Table.Th>
                    <Group
                      gap={4}
                      wrap="nowrap"
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => toggleSort('reference')}
                    >
                      <span>Référence</span>
                      {renderSortIndicators('reference')}
                    </Group>
                  </Table.Th>
                  <Table.Th miw={180}>Service</Table.Th>
                  <Table.Th w={110}>Statut</Table.Th>
                  <Table.Th w={100}>Prix</Table.Th>
                  <Table.Th visibleFrom="sm" w={160}>
                    <Group
                      gap={4}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => toggleSort('planned_date')}
                    >
                      <span>Date de remise</span>
                      {renderSortIndicators('planned_date')}
                    </Group>
                  </Table.Th>
                  <Table.Th w={160}>Statut commande</Table.Th>
                  <Table.Th w={40} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {orders.map((order) => (
                  <OrderRows
                    key={order.id}
                    order={order}
                    isBoutique={isBoutique}
                    organizationSlug={current_organization?.slug}
                  />
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Paper>

        {pagination.total_pages > 1 && (
          <Center>
            <Pagination total={pagination.total_pages} value={pagination.current_page} onChange={handlePageChange} />
          </Center>
        )}

        <Text size="sm" c="dimmed" ta="center">
          {pagination.total_count} commande{pagination.total_count > 1 ? 's' : ''}
        </Text>
      </Stack>
    </>
  )
}
